import { useEffect, useRef, useState } from 'react'
import type { CourseWithoutBlocks } from '@/types/course'
import type { Lesson } from '@/types/lesson'
import { useDeleteLessonMutation } from '@/features/editor/lessonsApi'
import { useDeleteBlockMutation } from '@/features/editor/blocksApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectBlock, selectLesson, toggleBlockSelection } from '@/features/editor/editorSlice'
import { useSaveStatusWrap } from '@/features/editor/hooks/useSaveStatusWrap'
import { useVideoUploads } from '@/features/editor/components/shared/VideoUploadContext'
import { useCourseJob } from '@/features/ai/useCourseJob'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { refusedByReview } from '@/features/editor/lib/reviewGuard'
import { useBlocksInRun, useIsBlockWritingRunActive } from './useBlocksInRun'
import { useLessonCreator } from './useLessonCreator'
import { useLessonReorder } from './useLessonReorder'
import { useLiveLessons } from './useLiveLessons'
import { isSameBlock } from './blockDraftSync'
import { messagesNow } from '@/i18n'
import { messages } from './messages'
import { toastIfUploadLost } from './uploadLostToast'

export const useCourseTimeline = (course: CourseWithoutBlocks) => {
  const dispatch = useAppDispatch()
  const contentLang = useAppSelector((s) => s.editor.contentLang)
  const selectedLessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const selectedBlockId = useAppSelector((s) => s.editor.selectedBlockId)
  const idAliases = useAppSelector((s) => s.editor.idAliases)
  // п.10 бэк-лога: набор мультивыбора для правки промптом — миниатюра рисует
  // отметку, `editorSlice` его же и сбрасывает при смене урока.
  const selectedBlockIds = useAppSelector((s) => s.editor.selectedBlockIds)

  useLiveLessons(course.id)
  const { entry, isPolling, isStatusUnknown } = useCourseJob(course.id)
  // "+ урок" прячется, только пока идёт СВОЯ генерация курса: порядок урока
  // задаёт API в момент записи (max(order) + 1), и урок, добавленный руками
  // посреди прогона, встал бы вперемешку с генерируемыми. Чужой прогон
  // (картинки/правка блока/перевод) уроков не создаёт — не гейтит кнопку.
  //
  // M-10: `isPolling` истинно и когда статус ЕЩЁ НЕ ПОДТВЕРЖДЁН — запись могла
  // пролежать в persist неделю. Прятать по ней кнопку значит на входе в
  // редактор с протухшей записью убирать «+ урок» на секунду без причины;
  // ждём первого ответа джобы.
  const isGeneratingLessons = entry?.type === 'course_generation' && isPolling && !isStatusUnknown
  const blocksInRun = useBlocksInRun(course.id)
  // Task 10 (Б): гейт реордера — грубее blocksInRun, у картиночного прогона
  // цели известны только серверу.
  const isBlockWritingRunActive = useIsBlockWritingRunActive(course.id)
  // Состав курса (уроки и их порядок) — часть заявки на проверку.
  const isFrozen = useIsCourseFrozen(course.id)

  const [deleteLesson] = useDeleteLessonMutation()
  const [deleteBlock] = useDeleteBlockMutation()
  const videoUploads = useVideoUploads()

  const [expandedLessonId, setExpandedLessonId] = useState<number | null>(selectedLessonId)
  const { addLesson, isCreatingLesson } = useLessonCreator(course, setExpandedLessonId)

  useEffect(() => {
    if (selectedLessonId != null && expandedLessonId !== selectedLessonId) {
      setExpandedLessonId(selectedLessonId)
    }
  }, [selectedLessonId, expandedLessonId])

  const selectedRef = useRef<HTMLDivElement>(null)
  const didMountRef = useRef(false)

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    selectedRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }, [selectedBlockId])

  const wrap = useSaveStatusWrap()

  // Clicking a lesson square expands it and selects the LESSON (clears the block
  // selection). On a lesson CHANGE `useLessonLastBlock` then selects its last block; a
  // repeat click on the same lesson keeps the block cleared and shows the lesson info.
  const handleExpand = (lessonId: number) => {
    setExpandedLessonId(lessonId)
    dispatch(selectLesson(lessonId))
  }

  const selectBlockInLesson = (lessonId: number, blockId: number) => {
    dispatch(selectLesson(lessonId))
    dispatch(selectBlock(blockId))
  }

  const toggleBlockSelected = (blockId: number) => dispatch(toggleBlockSelection(blockId))

  const reorderLessonsHandler = useLessonReorder(course)

  const addLessonHandler = async () => {
    if (refusedByReview(isFrozen)) return
    await addLesson()
  }

  const deleteLessonHandler = (lesson: Omit<Lesson, 'blocks'>) => {
    if (refusedByReview(isFrozen)) return
    const aborted = videoUploads.abortLesson(lesson.id)
    if (selectedLessonId === lesson.id) dispatch(selectLesson(null))
    if (expandedLessonId === lesson.id) setExpandedLessonId(null)
    void wrap(
      deleteLesson({ id: lesson.id, courseId: course.id }).unwrap(),
      messagesNow(messages).deleteLessonFailed,
    ).then((removed) => {
      toastIfUploadLost(aborted, removed, 'lesson')
    })
  }

  // Корзина миниатюры гаснет по `isBlockLocked` (на проверке заперты все
  // блоки); здесь страховка от клика за миг до того, как пришёл `pending`.
  // `next` — что выделить вместо удалённого (блок слева, `blockLeftOf`; `null` —
  // урок): считает строка урока, у неё есть список блоков.
  const deleteBlockInLesson = (
    lesson: Omit<Lesson, 'blocks'>,
    blockId: number,
    next: number | null = null,
  ) => {
    if (refusedByReview(isFrozen)) return
    // Через алиасы: у живого курса выделение может стоять на id другого
    // источника (черновик ⇄ живой), и строгое `===` оставляло его на удалённом
    // блоке — панель падала на урок вместо соседа слева.
    if (isSameBlock(idAliases, blockId, selectedBlockId)) dispatch(selectBlock(next))
    // Отмена сразу, а не после ответа сервера: за это время загрузка успевала кончиться.
    const aborted = videoUploads.abortBlock(blockId)
    void wrap(
      deleteBlock({ id: blockId, lessonId: lesson.id, courseId: course.id }).unwrap(),
      messagesNow(messages).deleteBlockFailed,
    ).then((removed) => {
      toastIfUploadLost(aborted, removed, 'block')
    })
  }

  return {
    contentLang,
    selectedLessonId,
    selectedBlockId,
    selectedBlockIds,
    expandedLessonId,
    isCreatingLesson,
    isGeneratingLessons,
    blocksInRun,
    isBlockWritingRunActive,
    isFrozen,
    selectedRef,
    addLesson: addLessonHandler,
    handleExpand,
    selectBlockInLesson,
    toggleBlockSelected,
    deleteLesson: deleteLessonHandler,
    deleteBlockInLesson,
    reorderLessons: reorderLessonsHandler,
    onLessonBlockCreated: selectBlockInLesson,
  }
}
